/**
 * ID Resolver utilities for managing prefixed article identifiers
 * Handles conversion between different ID formats and Wikipedia conventions
 */

import { ArticleId, ParsedArticleId, WikipediaServiceError } from './types';

/**
 * Utility class for handling prefixed article IDs
 * Provides parsing, validation, and conversion methods
 */
export class IdResolver {
  /**
   * Parse a prefixed article ID into its components
   * @param id Prefixed article identifier
   * @returns Parsed ID components
   * @throws WikipediaServiceError for invalid formats
   */
  static parse(id: ArticleId): ParsedArticleId {
    if (!id || typeof id !== 'string') {
      throw new WikipediaServiceError(
        'Invalid article ID: must be a non-empty string',
        'INVALID_ID',
        id?.toString()
      );
    }

    const colonIndex = id.indexOf(':');
    if (colonIndex === -1) {
      throw new WikipediaServiceError(
        `Invalid article ID format: missing prefix. Expected format: "type:value" (e.g., "page:123" or "title:Article Name")`,
        'INVALID_ID',
        id
      );
    }

    const type = id.slice(0, colonIndex) as 'page' | 'title' | 'temp';
    const value = id.slice(colonIndex + 1);

    if (!value) {
      throw new WikipediaServiceError(
        `Invalid article ID: empty value after prefix "${type}:"`,
        'INVALID_ID',
        id
      );
    }

    // Validate type
    if (!['page', 'title', 'temp'].includes(type)) {
      throw new WikipediaServiceError(
        `Invalid article ID prefix: "${type}". Supported prefixes: page, title, temp`,
        'INVALID_ID',
        id
      );
    }

    // Additional validation for page IDs
    if (type === 'page') {
      const pageId = parseInt(value, 10);
      if (isNaN(pageId) || pageId <= 0) {
        throw new WikipediaServiceError(
          `Invalid page ID: "${value}". Page IDs must be positive integers`,
          'INVALID_ID',
          id
        );
      }
    }

    // Additional validation for titles
    if (type === 'title') {
      if (value.length > 255) {
        throw new WikipediaServiceError(
          `Invalid title: too long (${value.length} chars, max 255)`,
          'INVALID_ID',
          id
        );
      }
      
      // Check for obviously invalid characters
      const invalidChars = ['<', '>', '[', ']', '|', '{', '}'];
      const foundInvalid = invalidChars.find(char => value.includes(char));
      if (foundInvalid) {
        throw new WikipediaServiceError(
          `Invalid title: contains invalid character "${foundInvalid}"`,
          'INVALID_ID',
          id
        );
      }
    }

    return {
      type,
      value,
      originalId: id
    };
  }

  /**
   * Check if an ID represents a Wikipedia page ID
   */
  static isPageId(id: ArticleId): boolean {
    try {
      return this.parse(id).type === 'page';
    } catch {
      return false;
    }
  }

  /**
   * Check if an ID represents an article title
   */
  static isTitle(id: ArticleId): boolean {
    try {
      return this.parse(id).type === 'title';
    } catch {
      return false;
    }
  }

  /**
   * Check if an ID represents a temporary card
   */
  static isTempId(id: ArticleId): boolean {
    try {
      return this.parse(id).type === 'temp';
    } catch {
      return false;
    }
  }

  /**
   * Extract numeric page ID from page: prefixed ID
   * @param id Article ID (must be page: prefixed)
   * @returns Numeric page ID
   * @throws WikipediaServiceError if not a valid page ID
   */
  static extractPageId(id: ArticleId): number {
    const parsed = this.parse(id);
    if (parsed.type !== 'page') {
      throw new WikipediaServiceError(
        `Expected page ID, got ${parsed.type}: ${id}`,
        'INVALID_ID',
        id
      );
    }
    return parseInt(parsed.value, 10);
  }

  /**
   * Extract title from title: prefixed ID
   * @param id Article ID (must be title: prefixed)
   * @returns Article title
   * @throws WikipediaServiceError if not a valid title ID
   */
  static extractTitle(id: ArticleId): string {
    const parsed = this.parse(id);
    if (parsed.type !== 'title') {
      throw new WikipediaServiceError(
        `Expected title ID, got ${parsed.type}: ${id}`,
        'INVALID_ID',
        id
      );
    }
    return parsed.value;
  }

  /**
   * Create a page: prefixed ID from numeric page ID
   */
  static createPageId(pageId: number): ArticleId {
    if (!Number.isInteger(pageId) || pageId <= 0) {
      throw new WikipediaServiceError(
        `Invalid page ID: must be a positive integer, got ${pageId}`,
        'INVALID_ID',
        pageId.toString()
      );
    }
    return `page:${pageId}` as ArticleId;
  }

  /**
   * Create a title: prefixed ID from article title
   */
  static createTitleId(title: string): ArticleId {
    if (!title || typeof title !== 'string') {
      throw new WikipediaServiceError(
        'Invalid title: must be a non-empty string',
        'INVALID_ID',
        title?.toString()
      );
    }
    
    // Normalize title (trim whitespace, normalize spaces)
    const normalized = title.trim().replace(/\s+/g, ' ');
    if (!normalized) {
      throw new WikipediaServiceError(
        'Invalid title: cannot be empty or whitespace only',
        'INVALID_ID',
        title
      );
    }
    
    return `title:${normalized}` as ArticleId;
  }

  /**
   * Create a temp: prefixed ID for temporary cards
   */
  static createTempId(identifier: string): ArticleId {
    if (!identifier || typeof identifier !== 'string') {
      throw new WikipediaServiceError(
        'Invalid temp identifier: must be a non-empty string',
        'INVALID_ID',
        identifier?.toString()
      );
    }
    return `temp:${identifier}` as ArticleId;
  }

  /**
   * Try to intelligently convert a legacy string ID to proper prefixed format
   * Used for migration from title-based IDs to prefixed system
   */
  static fromLegacyId(legacyId: string): ArticleId {
    if (!legacyId || typeof legacyId !== 'string') {
      throw new WikipediaServiceError(
        'Invalid legacy ID: must be a non-empty string',
        'INVALID_ID',
        legacyId?.toString()
      );
    }

    // If already prefixed, validate and return
    if (legacyId.includes(':')) {
      try {
        this.parse(legacyId as ArticleId);
        return legacyId as ArticleId;
      } catch {
        // Fall through to treat as title
      }
    }

    // Check if it's a pure numeric ID (likely page ID)
    const numericMatch = legacyId.match(/^\d+$/);
    if (numericMatch) {
      const pageId = parseInt(legacyId, 10);
      return this.createPageId(pageId);
    }

    // Check if it looks like a Wikipedia URL
    const urlMatch = legacyId.match(/\/wiki\/(.+)$/);
    if (urlMatch) {
      const title = decodeURIComponent(urlMatch[1].replace(/_/g, ' '));
      return this.createTitleId(title);
    }

    // Default: treat as title
    return this.createTitleId(legacyId);
  }

  /**
   * Convert article ID to Wikipedia URL path
   * @param id Article identifier
   * @returns URL path like "/wiki/Article%20Name"
   */
  static toUrlPath(id: ArticleId, title?: string): string {
    const parsed = this.parse(id);
    
    if (parsed.type === 'temp') {
      // Temporary cards don't have Wikipedia URLs
      return '/temp/' + encodeURIComponent(parsed.value);
    }

    // For page IDs, we need the title to create the URL
    if (parsed.type === 'page' && !title) {
      throw new WikipediaServiceError(
        'Cannot create URL for page ID without title',
        'INVALID_ID',
        id
      );
    }

    const urlTitle = (parsed.type === 'title' ? parsed.value : title)!;
    const encodedTitle = encodeURIComponent(urlTitle);
    return `/wiki/${encodedTitle}`;
  }

  /**
   * Normalize article title for consistent comparison and storage
   */
  static normalizeTitle(title: string): string {
    if (!title || typeof title !== 'string') {
      throw new WikipediaServiceError(
        'Invalid title for normalization',
        'INVALID_ID',
        title?.toString()
      );
    }

    const normalized = title
      .trim()                          // Remove leading/trailing whitespace
      .replace(/\s+/g, ' ')           // Normalize multiple spaces to single space
      .replace(/_/g, ' ');            // Convert underscores to spaces (URL format)
    
    // Check if the normalized title is empty
    if (!normalized) {
      throw new WikipediaServiceError(
        'Invalid title: cannot be empty or whitespace only',
        'INVALID_ID',
        title
      );
    }

    return normalized;
  }

  /**
   * Validate that a string could be a valid Wikipedia article title
   */
  static isValidTitle(title: string): boolean {
    try {
      const normalized = this.normalizeTitle(title);
      
      // Check length
      if (normalized.length === 0 || normalized.length > 255) {
        return false;
      }

      // Check for invalid characters
      const invalidChars = ['<', '>', '[', ']', '|', '{', '}', '#'];
      if (invalidChars.some(char => normalized.includes(char))) {
        return false;
      }

      // Check for invalid patterns
      if (normalized.startsWith(':') || normalized.endsWith(':')) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Compare two article IDs for equality
   * Handles normalization and cross-type comparison when possible
   */
  static equals(id1: ArticleId, id2: ArticleId): boolean {
    if (id1 === id2) return true;

    try {
      const parsed1 = this.parse(id1);
      const parsed2 = this.parse(id2);

      // Same type comparison
      if (parsed1.type === parsed2.type) {
        if (parsed1.type === 'title') {
          // Normalize titles for comparison
          return this.normalizeTitle(parsed1.value) === this.normalizeTitle(parsed2.value);
        }
        return parsed1.value === parsed2.value;
      }

      // Cross-type comparison not supported without lookup
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Generate a hash for an article ID for use in Maps/Sets
   * Ensures normalized titles have consistent hashes
   */
  static hash(id: ArticleId): string {
    try {
      const parsed = this.parse(id);
      if (parsed.type === 'title') {
        const normalized = this.normalizeTitle(parsed.value);
        return `title:${normalized}`;
      }
      return id;
    } catch {
      return id;
    }
  }

  /**
   * Get a human-readable description of an article ID
   * Useful for logging and debugging
   */
  static describe(id: ArticleId): string {
    try {
      const parsed = this.parse(id);
      switch (parsed.type) {
        case 'page':
          return `Wikipedia page ID ${parsed.value}`;
        case 'title':
          return `Wikipedia article "${parsed.value}"`;
        case 'temp':
          return `Temporary card ${parsed.value}`;
        default:
          return `Unknown article type: ${id}`;
      }
    } catch (error) {
      return `Invalid article ID: ${id} (${error instanceof Error ? error.message : 'unknown error'})`;
    }
  }
}

/**
 * Collection of ID resolver utilities exported as a namespace
 * Provides both class methods and standalone functions
 */
export const ArticleIdUtils = {
  parse: IdResolver.parse,
  isPageId: IdResolver.isPageId,
  isTitle: IdResolver.isTitle,
  isTempId: IdResolver.isTempId,
  extractPageId: IdResolver.extractPageId,
  extractTitle: IdResolver.extractTitle,
  createPageId: IdResolver.createPageId,
  createTitleId: IdResolver.createTitleId,
  createTempId: IdResolver.createTempId,
  fromLegacyId: IdResolver.fromLegacyId,
  toUrlPath: IdResolver.toUrlPath,
  normalizeTitle: IdResolver.normalizeTitle,
  isValidTitle: IdResolver.isValidTitle,
  equals: IdResolver.equals,
  hash: IdResolver.hash,
  describe: IdResolver.describe,
} as const;